'use client';

import { useRef } from 'react';
import { cn } from '../../../utilities/classname';
import { scrollStripBy, type FilmstripContainer } from './filmstrip-scroll';
import { StripArrows } from './strip-arrows';
import { useFilmstripOverflow } from './use-filmstrip-overflow';

/**
 * THE CLIENT BOUNDARY of the service filmstrip, and nothing else.
 *
 * It measures overflow and scroll position, and owns the scroller ref, the
 * arrows, and the step between them. The heading arrives through `header` and
 * the cards arrive through `children`, both already rendered on the server — so
 * a strip of six service cards ships its photos, its copy and both of its links
 * as HTML, and the only JavaScript the section costs is the scrolling behaviour.
 *
 * MUST BE RENDERED FULL-BLEED, for the reason `PortfolioFilmstrip` documents at
 * length: the arrows belong to the contained header row, the scroller they drive
 * bleeds past the page margin, and a ref cannot cross between two sibling
 * subtrees rendered by a server parent — so one client component has to own
 * both. It takes the page's container as a prop rather than sitting inside it.
 *
 * A `<ul>`, because its children are `ServiceCard`s and those are `<li>`s: the
 * strip is the same list the grid is, laid out along the other axis.
 *
 * Snapping stays PROXIMITY, again as the portfolio strip explains: `mandatory`
 * refuses to rest between snap points, which forces `scrollLeft` off zero at
 * load and eats the left inset that aligns tile 1 with the heading.
 */
export const ServiceFilmstripScroller: React.FC<{
  header: React.ReactNode;
  container: FilmstripContainer;
  /**
   * The accessible names of the two arrows, in full.
   *
   * REQUIRED, and required to be distinct per strip, because a page renders one
   * of these per service category — "More services" four times over is four
   * buttons a screen-reader user cannot tell apart in a list of controls.
   */
  prevLabel: string;
  nextLabel: string;
  children: React.ReactNode;
}> = ({ header, container, prevLabel, nextLabel, children }) => {
  const stripRef = useRef<HTMLUListElement>(null);
  const { hasOverflow, canScrollPrev, canScrollNext } = useFilmstripOverflow(stripRef, children);

  /**
   * All of the stepping lives in {@link scrollStripBy}, ref-null case included.
   * Its clamp is also the real guard at either end: an `aria-disabled` arrow
   * still receives the press, which then goes nowhere.
   */
  const step = (dir: 1 | -1) => { scrollStripBy(stripRef.current, dir); };

  return (
    <>
      <div className={cn(container.className, 'flex flex-col gap-8 pb-10 sm:flex-row sm:items-end sm:justify-between')}>
        {/*
          THE HEADER SLOT HAS TO BE TOLD TO GROW. `SectionHeading` — what every
          caller puts in here — is a `@container`, and `container-type:
          inline-size` applies inline-axis containment: the element's intrinsic
          contribution is ZERO, so as a bare flex item it resolves to a 0px-wide
          box whose text spills out one word per line. `grow` gives it the row's
          free space instead, which its own `max-w-*` then caps; `min-w-0` keeps
          a long unbroken word from pushing the arrows off the end.
        */}
        <div className="min-w-0 grow">{header}</div>

        {hasOverflow && (
          <StripArrows
            prevLabel={prevLabel}
            nextLabel={nextLabel}
            canPrev={canScrollPrev}
            canNext={canScrollNext}
            onStep={step}
          />
        )}
      </div>

      {/*
        `items-stretch` is the flex default and is what keeps every card in the
        row the same height as the tallest — the one thing the wrapping grid got
        for free from its track and a strip has to be left alone to do.
      */}
      <ul
        ref={stripRef}
        className={cn('flex gap-8 overflow-x-auto snap-x snap-proximity pt-2 pb-6', container.inset)}
      >
        {children}
      </ul>
    </>
  );
};
